// The speakers of the locked Masterclass videos, once each, with their talks (fullstack
// claude/UNLOCK-MASTERCLASS-SPEC.md, 2026-10-31). The videos come from showcases-lite.json's
// Masterclass collections (masterclasses.json): the speakers are the video's name before its
// first ":", split on "&" (a joint talk), the title what follows. A speaker is matched by the
// folded name (lower case, no diacritics, as speakers.json's keys) to speakers.json (the
// display name) and to the years' speaker sheets (the affiliation and the portrait's year, the
// latest year with a row). The figures a registration unlocks come from stats.json.
import {ref, onMounted} from "vue";
import {fetchProgramSheet} from "@/composables/useProgramSheet";

const fold = (name) => name.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
const getYear = (collection) => Number(collection.match(/\d{4}/)?.[0]) || 0;
const toPortrait = (name, year) => {
  return `/events/${year}/people/images/portraits/${name.replace(/ /g, "_")}.jpg`;
};

async function fetchJson(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch ${url}`);
  return response.json();
}

// a video's talk: {year, hash, title, speakers}
function getTalk(year, hash, video) {
  const [who, ...rest] = String(video.name || "").split(":");
  const speakers = who.split("&").map((name) => name.trim()).filter(Boolean);
  return {year, hash, title: rest.join(":").trim(), speakers};
}

// a year's videos in another showcase that are Masterclass talks too: video.collections (from
// shared/videos/collectionOverlaps.json; e.g. Brady Johnston's, in "VIZBI 2026": Seán, 2026-11-03)
function getOverlapVideos(showcases, year) {
  const isMasterclass = ([, video]) => video?.collections?.includes("masterclass");
  const yearShowcases = Object.entries(showcases).filter(([name]) => name.endsWith(` ${year}`));
  const entries = yearShowcases.flatMap(([, showcase]) => Object.entries(showcase?.videos || {}));
  return entries.filter(isMasterclass);
}

// the talks of the Masterclass collections, and their years' videos in both collections
function getTalks(showcases, collections) {
  return collections.flatMap((collection) => {
    const year = getYear(collection);
    const own = Object.entries(showcases[collection]?.videos || {});
    const ownHashes = new Set(own.map(([hash]) => hash));
    const shared = getOverlapVideos(showcases, year).filter(([hash]) => !ownHashes.has(hash));
    const talks = own.map(([hash, video]) => getTalk(year, hash, video));
    // a shared talk names its speaker, but isn't counted: stats.json counts it as Workshop
    const toShared = ([hash, video]) => ({...getTalk(year, hash, video), isShared: true});
    const sharedTalks = shared.map(toShared);
    return [...talks, ...sharedTalks];
  });
}

// a year's speaker sheet ([headers, ...rows]) as {name, affiliation, year}
function parseSheet(data, year) {
  const [headers, ...rows] = data;
  const index = Object.fromEntries(headers.map((header, i) => [header, i]));
  const read = (row, column) => String(row[index[column]] || "").trim();
  const people = rows.map((row) => {
    return {name: read(row, "Name"), affiliation: read(row, "Affiliation"), year};
  });
  return people.filter((person) => person.name);
}

// the talks by speaker (folded name), a joint talk under each of its speakers
function groupBySpeaker(talks) {
  const groups = new Map();
  for (const talk of talks) {
    for (const speaker of talk.speakers) {
      const key = fold(speaker);
      groups.set(key, [...(groups.get(key) || []), talk]);
    }
  }
  return groups;
}

// one line for the speaker's talks (Seán, 2026-10-31; no years): masterclassTalks.json's title
// (one per speaker, covering all their talks), else the latest talk's; a single talk's video
function getLine(talks, title) {
  const [latest] = talks;
  const hash = talks.length === 1 ? latest.hash : "";
  return {title: title || latest.title, hash};
}

// one speaker: the sheets' name and affiliation (the latest year with a row; the sheets are
// newest first; the portrait's year), else speakers.json's name, else the video's spelling;
// the talks newest first, and the one line shown for them
function buildSpeaker(folded, talks, {index, rows, titles}) {
  const row = rows.find((person) => fold(person.name) === folded);
  const spelled = talks[0].speakers.find((speaker) => fold(speaker) === folded);
  const name = row?.name || index[folded]?.name || spelled;
  const sorted = [...talks].sort((a, b) => b.year - a.year);
  const year = row?.year || sorted[0].year;
  const own = sorted.map(({year: talkYear, title, hash}) => ({year: talkYear, title, hash}));
  const affiliation = row?.affiliation || "";
  const line = getLine(own, titles[folded]);
  return {name, affiliation, year, image: toPortrait(name, year), talks: own, line};
}

// alphabetical by surname, the name's last word (Seán, 2026-10-31; before: most talks first)
const getSurname = (name) => name.trim().split(" ").pop();
const bySpeaker = (a, b) => {
  return getSurname(a.name).localeCompare(getSurname(b.name)) || a.name.localeCompare(b.name);
};

async function fetchSources(collections) {
  const years = [...new Set(collections.map(getYear))].sort((a, b) => b - a);
  const fetchSheet = (year) => {
    return fetchProgramSheet(year).then(({speakers}) => (speakers ? parseSheet(speakers, year) : []));
  };
  const [showcases, index, sheets, stats, titles] = await Promise.all([
    fetchJson("/sitemaps/videos/showcases-lite.json").catch(() => ({})),
    fetchJson("/sitemaps/videos/speakers.json").catch(() => ({})),
    Promise.all(years.map(fetchSheet)),
    fetchJson("/sitemaps/videos/stats.json").catch(() => ({})),
    fetchJson("/sitemaps/videos/masterclassTalks.json").catch(() => ({})),
  ]);
  return {showcases, index, rows: sheets.flat(), stats, titles, years};
}

export default function useMasterclassSpeakers() {
  const speakers = ref([]);
  const talkCount = ref(0);
  const years = ref([]);
  const stats = ref(null); // {videos, hours}, or null: the figures hidden
  const isLoading = ref(true);
  onMounted(async () => {
    const collections = await fetchJson("/sitemaps/videos/masterclasses.json").catch(() => []);
    const sources = await fetchSources(collections);
    const talks = getTalks(sources.showcases, collections);
    const groups = groupBySpeaker(talks);
    const build = ([folded, own]) => buildSpeaker(folded, own, sources);
    const built = [...groups].map(build);
    speakers.value = built.sort(bySpeaker);
    talkCount.value = talks.filter((talk) => !talk.isShared).length; // as stats.json
    years.value = sources.years;
    stats.value = sources.stats.masterclass || null;
    isLoading.value = false;
  });
  return {speakers, talkCount, years, stats, isLoading};
}
